import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ChevronLeft,
  Plus,
  Trash2,
  Search,
  Hash,
  Info,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePickerField } from '@/shared/ui/components';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useSearchInsurancePayers } from '@/patients/services/use-search-insurance-payers';
import { useSaveHealthInsurance } from '@/patients/services/use-save-health-insurance';
import { useBillingSettings } from '@/billing/services/use-billing-settings';
import { IMAGE_AREAS } from '@/services/common/use-upload-image';
import { PayerPhotoUploader } from '@/shared/ui/components/payer-photo-uploader';
import { PayerCopayTable } from '@/shared/ui/components/payer-copay-table';
import { usePreBookSelectedPatient } from '@/services/scheduling/use-prebook-selected-patient';
import type {
  HealthInsurance,
  PayerBillingGroup,
  PayerDetails,
} from '@/services/scheduling/use-prebook-selected-patient';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { BillingGroup } from '@/shared/types/billing';

/**
 * EditInsuranceView — full-content replacement of the create-appointment
 * panel for editing the patient's health insurance list.
 *
 * Flutter parity for PROD-708. Combines:
 *   - `edit_health_insurance_profile_widget.dart` — overlay shell
 *   - `health_insurance_widget.dart` — list of compact rows
 *   - `edit_health_insurance_widget.dart` — per-row inline editor
 *
 * UX pattern: list-then-expand. The view shows compact summary cards
 * for every insurance entry; tapping a card's "Edit" button expands
 * that single row into the full editor inline. Done commits the row;
 * Cancel discards the row's edits; Trash removes the row. The
 * panel-level Save button at the top right pushes the whole list to
 * `POST /api/Patients/HealthInsurance` and re-fires PreBookSelectedPatient.
 */

interface EditInsuranceViewProps {
  onClose: () => void;
  organizationId: string;
  practiceId: string;
}

const MAX_INSURANCE_ROWS = 2;

function shortDate(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'N/A';
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function toInputDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isExpiredIso(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
}

function payerName(payer: PayerDetails | undefined): string {
  return payer?.payerName ?? payer?.name ?? '';
}

function placeholderInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

/** Insurance + TPA codes line — Flutter `health_insurance_widget.dart:422`.
 *  Format: `Insurance: {payerCompanyId} • TPA: {relatedTpa[0].code}`
 *  TPA suffix only when relatedTpa has at least one entry. */
function insuranceCodes(row: HealthInsurance): string {
  const provider = row.insuranceProvider as
    | (PayerDetails & {
        payerCompanyId?: string;
        relatedTpa?: { code?: string }[];
      })
    | undefined;
  const insCode = provider?.payerCompanyId ?? '';
  const tpaCode = provider?.relatedTpa?.[0]?.code ?? '';
  if (insCode && tpaCode) return `Insurance: ${insCode} • TPA: ${tpaCode}`;
  if (insCode) return `Insurance: ${insCode}`;
  if (tpaCode) return `TPA: ${tpaCode}`;
  return '';
}

// ── PayerTypeahead ──────────────────────────────────────────────────────────

interface PayerTypeaheadProps {
  organizationId: string;
  selected: PayerDetails | undefined;
  onSelect: (payer: PayerDetails) => void;
  onClear: () => void;
}

function PayerTypeahead({
  organizationId,
  selected,
  onSelect,
  onClear,
}: PayerTypeaheadProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounce(query, 300);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: payers = [], isPending } = useSearchInsurancePayers({
    organizationId,
    query: debounced,
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const showSelectedChip = !!selected && !open;

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
        {showSelectedChip ? (
          <div className="flex items-center pl-10 pr-10 h-10 rounded-md border border-border-default bg-grey-100 text-sm text-text-primary">
            <span className="truncate flex-1">{payerName(selected)}</span>
            <button
              type="button"
              onClick={onClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-brand-primary hover:text-interactive-hover"
              aria-label="Clear payer"
            >
              ×
            </button>
          </div>
        ) : (
          <Input
            type="text"
            value={query}
            placeholder="Search insurance provider"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="pl-10"
          />
        )}
      </div>
      {open ? (
        <div className="absolute left-0 right-0 mt-1 z-20 max-h-72 overflow-y-auto scrollbar-hide rounded-lg border border-border-default bg-surface-card shadow-md">
          {isPending && debounced.length >= 2 ? (
            <p className="px-3 py-2 text-sm text-text-muted">Searching…</p>
          ) : debounced.length < 2 ? (
            <p className="px-3 py-2 text-sm text-text-muted">
              Type 2+ characters to search
            </p>
          ) : payers.length === 0 ? (
            <p className="px-3 py-2 text-sm text-text-muted">No matches</p>
          ) : (
            payers.map((p, i) => {
              const name = payerName(p);
              const ext = p as PayerDetails & {
                payerCompanyId?: string;
                payerAgreementName?: string;
                relatedTpa?: { code?: string; companyName?: string }[];
              };
              const tpa = ext.relatedTpa?.[0];
              // Title — Flutter `field_and_label.dart:2298`:
              //   TPA-mediated: "{TPA companyName} ({payerName})"
              //   Direct:       "{payerName}"
              const title = tpa?.companyName
                ? `${tpa.companyName} (${name})`
                : name;
              // Codes — Flutter `field_and_label.dart:2316`:
              //   "{TPA code} • {payerCompanyId}"
              // `payerAgreementId` is the agreement (e.g. `INS041_GN`),
              // different concept; do NOT display here.
              const tpaCode = tpa?.code;
              const insCode = ext.payerCompanyId;
              const codes = [tpaCode, insCode].filter(Boolean).join(' • ');
              return (
                <button
                  key={p.id ?? `${name}-${i}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(p);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={cn(
                    'w-full flex items-start gap-3 px-3 py-3 text-left hover:bg-surface-sunken',
                    i > 0 && 'border-t border-border-subtle',
                  )}
                >
                  <div className="h-8 w-8 rounded-full bg-grey-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt={title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-bold text-text-secondary">
                        {placeholderInitials(title) || '—'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-text-primary leading-snug break-words">
                      {title}
                    </p>
                    {ext.payerAgreementName ? (
                      <p className="mt-1 text-sm text-text-secondary truncate">
                        {ext.payerAgreementName}
                      </p>
                    ) : null}
                    {codes ? (
                      <p className="mt-1 text-xs text-text-muted truncate">
                        {codes}
                      </p>
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── CompactInsuranceRow ────────────────────────────────────────────────────

interface CompactInsuranceRowProps {
  row: HealthInsurance;
  onEdit: () => void;
}

function CompactInsuranceRow({ row, onEdit }: CompactInsuranceRowProps) {
  const provider = row.insuranceProvider;
  const codes = insuranceCodes(row);
  const expired = isExpiredIso(row.expirationDate);
  return (
    <div className="rounded-xl border border-border-default bg-surface-card p-4 shadow-[var(--shadow-2)]">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-normal text-[var(--color-brand-depth)] leading-snug line-clamp-2 break-words">
            {payerName(provider) || '—'}
          </p>
          {row.policyNumber ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-text-secondary">
              <Hash className="h-4 w-4 text-text-muted flex-shrink-0" />
              <span className="truncate text-text-primary">{row.policyNumber}</span>
            </p>
          ) : null}
          {codes ? (
            <p className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
              <Info className="h-4 w-4 text-text-muted flex-shrink-0" />
              <span className="truncate text-text-primary">{codes}</span>
            </p>
          ) : null}
          {row.eligiblityStartDate ? (
            <p className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
              <Calendar className="h-4 w-4 text-text-muted flex-shrink-0" />
              <span className="truncate text-text-primary">
                Eligibility Start Date: {shortDate(row.eligiblityStartDate)}
              </span>
            </p>
          ) : null}
          <p className="mt-1 pl-6 text-sm text-text-primary">
            Expiration Date:{' '}
            {row.expirationDate ? (
              expired ? (
                <span className="font-semibold text-status-error">Expired</span>
              ) : (
                shortDate(row.expirationDate)
              )
            ) : (
              'N/A'
            )}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onEdit}
          className="flex-shrink-0"
        >
          Edit
        </Button>
      </div>
    </div>
  );
}

// ── InsuranceRowEditor ─────────────────────────────────────────────────────

interface InsuranceRowEditorProps {
  draft: HealthInsurance;
  organizationId: string;
  currencyCode: string;
  billingGroups: BillingGroup[];
  onChange: (next: HealthInsurance) => void;
  onDone: () => void;
  onCancel: () => void;
  onRemove: () => void;
  /** Done button enabled when validation + confirmation are both green. */
  canDone: boolean;
  errors: string[];
  confirmChecked: boolean;
  onToggleConfirm: () => void;
}

function InsuranceRowEditor({
  draft,
  organizationId,
  currencyCode,
  billingGroups,
  onChange,
  onDone,
  onCancel,
  onRemove,
  canDone,
  errors,
  confirmChecked,
  onToggleConfirm,
}: InsuranceRowEditorProps) {
  return (
    <div className="rounded-xl border border-border-default p-4 bg-surface-card space-y-4">
      {/* Card photos */}
      <div>
        <Label className="text-xs font-medium text-text-muted">
          Insurance card photos
        </Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <PayerPhotoUploader
            url={draft.frontPhotoUrl ?? null}
            label="Front Photo"
            side="front"
            titleNoun="insurance"
            area={IMAGE_AREAS.INSURANCE_CARD}
            onUploaded={(url) => onChange({ ...draft, frontPhotoUrl: url })}
            onClear={() => onChange({ ...draft, frontPhotoUrl: null })}
          />
          <PayerPhotoUploader
            url={draft.backPhotoUrl ?? null}
            label="Back Photo"
            side="back"
            titleNoun="insurance"
            area={IMAGE_AREAS.INSURANCE_CARD}
            onUploaded={(url) => onChange({ ...draft, backPhotoUrl: url })}
            onClear={() => onChange({ ...draft, backPhotoUrl: null })}
          />
        </div>
      </div>

      {/* Payer */}
      <div>
        <Label className="text-xs font-medium text-text-muted">
          Primary insurance provider <span className="text-status-error">*</span>
        </Label>
        <div className="mt-1">
          <PayerTypeahead
            organizationId={organizationId}
            selected={draft.insuranceProvider}
            onSelect={(payer) =>
              onChange({
                ...draft,
                insuranceProvider: payer,
                insuranceProviderId: payer.id,
              })
            }
            onClear={() =>
              onChange({
                ...draft,
                insuranceProvider: undefined,
                insuranceProviderId: undefined,
              })
            }
          />
        </div>
      </div>

      {/* Member ID */}
      <div>
        <Label className="text-xs font-medium text-text-muted">
          Member ID <span className="text-status-error">*</span>
        </Label>
        <Input
          type="text"
          value={draft.policyNumber ?? ''}
          onChange={(e) => onChange({ ...draft, policyNumber: e.target.value })}
          placeholder="Member ID"
          className="mt-1"
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium text-text-muted">
            Eligibility start date
          </Label>
          <DatePickerField
            value={toInputDate(draft.eligiblityStartDate)}
            onChange={(v) =>
              onChange({
                ...draft,
                // PROD-1778 hardening: was `new Date(v).toISOString()`.
                // Accidentally day-correct (`new Date('YYYY-MM-DD')` parses as
                // UTC midnight, and the save hook stripped the Z) but it relied
                // on two bugs cancelling. Ship the day-preserving wire form
                // directly — same shape as health-insurance-row.tsx:346.
                eligiblityStartDate: v ? `${v}T00:00:00.000` : null,
              })
            }
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-text-muted">
            Expiration date <span className="text-status-error">*</span>
          </Label>
          <DatePickerField
            value={toInputDate(draft.expirationDate)}
            onChange={(v) =>
              onChange({
                ...draft,
                // PROD-1778 hardening — see the eligibility-start note above.
                expirationDate: v ? `${v}T00:00:00.000` : null,
              })
            }
            className="mt-1"
          />
        </div>
      </div>

      {/* Co-pay header (label + type pills + Select All) + table */}
      {billingGroups.length > 0 ? (
        <PayerCopayTable<PayerBillingGroup>
          label="Co-Pay"
          showMaxCopay
          billingGroups={billingGroups}
          selected={draft.billingGroups ?? []}
          onChange={(next) => onChange({ ...draft, billingGroups: next })}
          copayType={draft.copaymentType}
          onCopayTypeChange={(next) => onChange({ ...draft, copaymentType: next })}
          currencyCode={currencyCode}
          makeGroup={(g) => ({
            billingGroupId: g.id,
            category: g.category,
            value: null,
            maximumCopayment: null,
          })}
        />
      ) : null}

      {/* Confirmation checkbox */}
      <label className="flex items-start gap-2 cursor-pointer">
        <span
          className={cn(
            'mt-1 h-4 w-4 rounded border-2 inline-flex items-center justify-center flex-shrink-0',
            confirmChecked
              ? 'border-brand-primary bg-brand-primary text-white'
              : 'border-border-default',
          )}
          onClick={onToggleConfirm}
          role="checkbox"
          aria-checked={confirmChecked}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleConfirm();
            }
          }}
        >
          {confirmChecked ? '✓' : null}
        </span>
        {/* TODO(i18n): compliance-adjacent copy hardcoded to match Flutter
            verbatim. If it becomes legally-managed, source from config/i18n. */}
        <span
          className="text-sm text-text-primary leading-snug"
          onClick={onToggleConfirm}
        >
          I confirm that the data entered matches that on the card. This
          insurance policy is accepted at this clinic.
        </span>
      </label>

      {errors.length > 0 ? (
        <ul className="text-sm text-status-error space-y-1">
          {errors.map((err) => (
            <li key={err}>• {err}</li>
          ))}
        </ul>
      ) : null}

      {/* Row-level actions */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          onClick={onDone}
          disabled={!canDone}
        >
          Done
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onRemove}
          className="p-1 rounded hover:bg-grey-100 text-status-error"
          aria-label="Remove insurance"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── EditInsuranceView ──────────────────────────────────────────────────────

interface DraftState {
  /** Index in localList that's being edited. -1 means none. */
  idx: number;
  draft: HealthInsurance;
  /** Snapshot of the row before edit — used to restore on Cancel. */
  original: HealthInsurance | null;
  /** Was this row newly created in this session? On cancel it's removed. */
  isNew: boolean;
  confirmChecked: boolean;
}

export function EditInsuranceView({
  onClose,
  organizationId,
  practiceId,
}: EditInsuranceViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const patientId = watch('patientId') as string | null;
  const location = watch('location');
  const billingProfileId = watch('billingProfileId') as string | null;
  const sponsorId = watch('sponsorId') as string | null;
  const sponsorType = watch('sponsorType');
  const remoteList = (watch('healthInsurances') ?? []) as HealthInsurance[];

  const auth = useAuthStore((s) => s.user);
  const orgId = organizationId || auth?.organizationId || '';
  const currencyCode = auth?.organizationCurrency ?? 'AED';

  const [localList, setLocalList] = useState<HealthInsurance[]>(() =>
    structuredClone(remoteList),
  );
  const [editing, setEditing] = useState<DraftState | null>(null);
  const [dirty, setDirty] = useState(false);

  const billingSettingsQ = useBillingSettings(orgId);
  const billingGroups = billingSettingsQ.data?.billingGroups ?? [];

  const saveMutation = useSaveHealthInsurance();
  const prebookMutation = usePreBookSelectedPatient();

  const activeList = useMemo(
    () => localList.filter((r) => !r.isDeleted),
    [localList],
  );

  const startEdit = (idx: number) => {
    if (editing) return;
    const row = localList[idx];
    setEditing({
      idx,
      draft: structuredClone(row),
      original: structuredClone(row),
      isNew: false,
      confirmChecked: !!row.healthInsuranceId,
    });
  };

  const addNewRow = () => {
    if (editing || activeList.length >= MAX_INSURANCE_ROWS) return;
    const isFirst = activeList.length === 0;
    const blank: HealthInsurance = {
      patientId: patientId ?? undefined,
      insuranceProvider: undefined,
      policyNumber: '',
      expirationDate: null,
      eligiblityStartDate: null,
      isPrimary: isFirst,
      isVerified: false,
      billingGroups: [],
    };
    const nextList = [...localList, blank];
    setLocalList(nextList);
    setEditing({
      idx: nextList.length - 1,
      draft: structuredClone(blank),
      original: null,
      isNew: true,
      confirmChecked: false,
    });
  };

  const updateDraft = (next: HealthInsurance) => {
    setEditing((prev) => (prev ? { ...prev, draft: next } : prev));
  };

  const toggleConfirm = () => {
    setEditing((prev) =>
      prev ? { ...prev, confirmChecked: !prev.confirmChecked } : prev,
    );
  };

  const validateDraft = (draft: HealthInsurance, draftIdx: number): string[] => {
    const errs: string[] = [];
    if (!draft.insuranceProvider?.id) errs.push('Insurance provider is required');
    if (!draft.policyNumber?.trim()) errs.push('Member ID is required');
    if (!draft.expirationDate) errs.push('Expiration date is required');
    // Duplicate-payer check vs the other rows in the list.
    const dupe = localList.some(
      (r, i) =>
        i !== draftIdx &&
        !r.isDeleted &&
        r.insuranceProvider?.id &&
        r.insuranceProvider.id === draft.insuranceProvider?.id,
    );
    if (dupe) errs.push('Health Insurance already added');
    // Co-pay group rows must have a non-zero value when selected.
    const selectedGroups = draft.billingGroups ?? [];
    if (selectedGroups.length > 0 && !draft.copaymentType) {
      errs.push('Select a co-pay type (Fixed or Pct)');
    }
    if (selectedGroups.some((g) => !g.value)) {
      errs.push('Please enter value for selected billing groups');
    }
    return errs;
  };

  const commitDraft = () => {
    if (!editing) return;
    const errs = validateDraft(editing.draft, editing.idx);
    if (errs.length > 0 || !editing.confirmChecked) return;

    setLocalList((prev) => {
      const out = prev.slice();
      // Stamp isVerified from the confirmation checkbox — backend reads
      // this flag from the wire payload (dev's working request shipped
      // isVerified: true after the user ticked the same checkbox).
      let next: HealthInsurance = {
        ...editing.draft,
        isVerified: editing.confirmChecked,
        isReviewed: true,
      };
      // If promoting to primary, demote all others.
      if (next.isPrimary) {
        for (let i = 0; i < out.length; i++) {
          if (i !== editing.idx) out[i] = { ...out[i], isPrimary: false };
        }
      }
      // First active row is force-primary.
      const activeCount = out.filter((r) => !r.isDeleted).length;
      if (activeCount === 1 || !out.some((r, i) => i !== editing.idx && !r.isDeleted && r.isPrimary)) {
        next = { ...next, isPrimary: true };
      }
      out[editing.idx] = next;
      return out;
    });
    setEditing(null);
    setDirty(true);
  };

  const cancelEdit = () => {
    if (!editing) return;
    if (editing.isNew) {
      // Drop the blank row added at startNewRow.
      setLocalList((prev) => {
        const out = prev.slice();
        out.splice(editing.idx, 1);
        return out;
      });
    } else if (editing.original) {
      // Restore the row to its original state.
      setLocalList((prev) => {
        const out = prev.slice();
        out[editing.idx] = editing.original!;
        return out;
      });
    }
    setEditing(null);
  };

  const removeEditingRow = () => {
    if (!editing) return;
    setLocalList((prev) => {
      const out = prev.slice();
      const row = out[editing.idx];
      if (row.healthInsuranceId) {
        out[editing.idx] = { ...row, isDeleted: true };
      } else {
        out.splice(editing.idx, 1);
      }
      return out;
    });
    setEditing(null);
    setDirty(true);
  };

  // Live validation surfaces inline errors only after the user has
  // actually started filling the row — show on every keystroke is fine
  // since the Done button is the actual gate.
  const draftErrors = editing ? validateDraft(editing.draft, editing.idx) : [];
  const canDone =
    !!editing &&
    draftErrors.length === 0 &&
    editing.confirmChecked &&
    !!editing.draft.insuranceProvider?.id;

  const canSave = dirty && editing === null && !saveMutation.isPending;

  const handleSave = async () => {
    if (!patientId) return;
    try {
      await saveMutation.mutateAsync({ patientId, insuranceList: localList });
      // The saved active list (excluding soft-deleted) becomes the new
      // form-level healthInsurances so the panel reflects the change
      // instantly.
      setValue('healthInsurances', activeList);
      const providerId = watch('providerId') as string | null;
      if (providerId && location?.id) {
        await prebookMutation.mutateAsync({
          patientId,
          providerId,
          locationId: location.id,
          billingProfileId,
          sponsorId,
          paymentType:
            sponsorType === 'Insurance'
              ? 'Insurance'
              : sponsorType === 'Corporate'
                ? 'Corporate'
                : 'Self-pay',
        });
      }
      toast.success('Insurance updated');
      onClose();
    } catch {
      // Global toast handler surfaces the error.
    }
  };

  void practiceId; // Reserved for future PreBook scoping if needed.

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
        <button
          type="button"
          onClick={onClose}
          className="p-1 -ml-1 rounded hover:bg-grey-100"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5 text-text-primary" />
        </button>
        <h2 className="text-base font-bold text-text-primary">Health Insurance</h2>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave || prebookMutation.isPending}
          className={cn(
            'text-sm font-semibold px-2',
            canSave && !prebookMutation.isPending
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          {saveMutation.isPending || prebookMutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 py-3 space-y-3">
        {activeList.length === 0 && !editing ? (
          <div className="rounded-xl border border-dashed border-border-default px-4 py-6 text-center">
            <p className="text-sm text-text-muted">
              No insurance on file for this patient.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={addNewRow}
            >
              <Plus className="h-4 w-4 mr-1" /> Add Insurance
            </Button>
          </div>
        ) : (
          <>
            {localList.map((row, idx) => {
              if (row.isDeleted) return null;
              if (editing && editing.idx === idx) {
                return (
                  <InsuranceRowEditor
                    key={row.healthInsuranceId ?? `editing-${idx}`}
                    draft={editing.draft}
                    organizationId={orgId}
                    currencyCode={currencyCode}
                    billingGroups={billingGroups}
                    onChange={updateDraft}
                    onDone={commitDraft}
                    onCancel={cancelEdit}
                    onRemove={removeEditingRow}
                    canDone={canDone}
                    errors={draftErrors}
                    confirmChecked={editing.confirmChecked}
                    onToggleConfirm={toggleConfirm}
                  />
                );
              }
              return (
                <CompactInsuranceRow
                  key={row.healthInsuranceId ?? `row-${idx}`}
                  row={row}
                  onEdit={() => startEdit(idx)}
                />
              );
            })}
            {activeList.length < MAX_INSURANCE_ROWS && !editing ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addNewRow}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Insurance
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
